import React from "react";

import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { Card } from "@excalidraw/excalidraw/components/Card";
import { ExcalidrawLogo } from "@excalidraw/excalidraw/components/ExcalidrawLogo";
import { ToolButton } from "@excalidraw/excalidraw/components/ToolButton";
import { DEFAULT_FILENAME, MIME_TYPES, getFrame } from "@excalidraw/common";
import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";
import { useI18n } from "@excalidraw/excalidraw/i18n";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

const DRIVE_UPLOAD_ENDPOINT =
  "https://drive.createstudio.app/api/v1/drive-custom";
const DRIVE_SIGNIN_ERROR =
  "You need to sign in to save this scene to Drive.";

const getSceneFileName = (name: string) => {
  const fallback = name?.trim() || DEFAULT_FILENAME;
  const sanitized = fallback.replace(/\.[^/.]+$/, "");
  return `${sanitized}.collabboard`;
};

const createSceneFile = (
  elements: readonly NonDeletedExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
  name: string,
) => {
  const serialized = serializeAsJSON(elements, appState, files, "local");
  const blob = new Blob([serialized], {
    type: MIME_TYPES.excalidraw,
  });
  try {
    return new File([blob], getSceneFileName(name), {
      type: MIME_TYPES.excalidraw,
    });
  } catch {
    return blob;
  }
};

export const exportToExcalidrawPlus = async (
  elements: readonly NonDeletedExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
  name: string,
  userEmail: string | null,
) => {
  if (!userEmail) {
    throw new Error(DRIVE_SIGNIN_ERROR);
  }
  const file = createSceneFile(elements, appState, files, name);

  const formData = new FormData();
  formData.append("email", userEmail);
  const filename =
    file instanceof File ? file.name : getSceneFileName(name || "");
  formData.append("file", file, filename);

  const response = await fetch(DRIVE_UPLOAD_ENDPOINT, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Unable to save the scene to Drive right now.");
  }
};

export const ExportToExcalidrawPlus: React.FC<{
  elements: readonly NonDeletedExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
  name: string;
  onError: (error: Error) => void;
  onSuccess: (message?: string) => void;
  userEmail?: string | null;
}> = ({
  elements,
  appState,
  files,
  name,
  onError,
  onSuccess,
  userEmail = null,
}) => {
  const { t } = useI18n();
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSaveToDrive = async () => {
    if (isSaving) {
      return;
    }
    if (!userEmail) {
      onError(new Error(DRIVE_SIGNIN_ERROR));
      return;
    }
    try {
      setIsSaving(true);
      trackEvent("export", "eplus", `ui (${getFrame()})`);
      await exportToExcalidrawPlus(
        elements,
        appState,
        files,
        name,
        userEmail,
      );
      onSuccess(t("toast.driveSaveSuccess"));
    } catch (error: any) {
      console.error(error);
      if (error.name !== "AbortError") {
        const message =
          error?.message || t("exportDialog.excalidrawplus_exportError");
        onError(new Error(message));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card color="primary">
      <div className="Card-icon">
        <ExcalidrawLogo
          style={{
            [`--color-logo-icon` as any]: "#fff",
            width: "2.8rem",
            height: "2.8rem",
          }}
        />
      </div>
      <h2>Collabboard+</h2>
      <div className="Card-details">
        {t("exportDialog.excalidrawplus_description")}
      </div>
      <ToolButton
        className="Card-button"
        type="button"
        title={t("exportDialog.excalidrawplus_button")}
        aria-label={t("exportDialog.excalidrawplus_button")}
        showAriaLabel={true}
        onClick={handleSaveToDrive}
        disabled={isSaving}
      />
    </Card>
  );
};
